import type { CommandBus } from '../bridge/CommandBus';
import { MINIGAMES } from '../sim/minigames';
import type { RanchCommand } from '../sim/ranch/commands';
import {
  FACILITY_DEFS,
  FEED_PLANS,
  FEED_PLAN_IDS,
  STABLE_DEF,
  stableStalls,
  type FacilityId,
} from '../sim/ranch/economy';
import { coatPaletteFor } from '../art/palettes';
import { cssColor } from '../render/colors';
import { coatPhenotype, gradeOf } from '../sim/ranch/genetics';
import { pedigreeOf } from '../sim/ranch/pedigree';
import { ageOf, horseValueCents, studFeeCents } from '../sim/ranch/horse';
import { TRAIT_DEFS } from '../sim/ranch/traits';
import { trainingEnergyCost } from '../sim/ranch/traits';
import type { Horse } from '../sim/ranch/types';
import type { StatId } from '../sim/race/types';

const STAT_ORDER: StatId[] = ['speed', 'stamina', 'accel', 'heart'];

/** Revealed grades read as letters, the rest stay a mystery. */
function gradeLine(horse: Horse): string {
  return STAT_ORDER.map((stat) =>
    horse.revealedGrades.includes(stat) ? gradeOf(horse.potential[stat]) : '?',
  ).join('');
}

function traitChips(horse: Horse): string {
  if (horse.revealedTraits.length === 0) return '';
  return horse.revealedTraits.map((trait) => TRAIT_DEFS[trait].name).join(', ');
}
import { xpToNext } from '../sim/ranch/horse';
import type { RanchSim } from '../sim/ranch/sim';
import { scheduleWindow } from '../sim/ranch/view';
import { el, formatMoney, formatTime } from './dom';

export interface ModalHandle {
  dispose(): void;
  /** Re-render from fresh sim state (called by the scene after events). */
  refresh(): void;
}

function openModal(root: HTMLElement, className: string, onClose: () => void): { backdrop: HTMLElement; body: HTMLElement } {
  const body = el('div', { class: `panel modal ${className}` });
  const backdrop = el('div', { class: 'modal-backdrop' }, body);
  backdrop.addEventListener('pointerdown', (event) => {
    if (event.target === backdrop) onClose();
  });
  const onKey = (event: KeyboardEvent): void => {
    if (event.code === 'Escape') {
      event.preventDefault();
      onClose();
    }
  };
  window.addEventListener('keydown', onKey);
  (backdrop as HTMLElement & { cleanupKey?: () => void }).cleanupKey = () =>
    window.removeEventListener('keydown', onKey);
  root.append(backdrop);
  return { backdrop, body };
}

function closeButton(onClose: () => void): HTMLElement {
  return el('button', { class: 'modal-close', onclick: () => onClose() }, '×');
}

function disposeOf(backdrop: HTMLElement): void {
  (backdrop as HTMLElement & { cleanupKey?: () => void }).cleanupKey?.();
  backdrop.remove();
}

/** Feed Shed: pick tomorrow's ration. */
export function showFeedModal(root: HTMLElement, sim: RanchSim, bus: CommandBus<RanchCommand>): ModalHandle {
  let disposed = false;
  const close = (): void => {
    disposed = true;
    disposeOf(backdrop);
  };
  const { backdrop, body } = openModal(root, 'feed-modal', () => close());

  const render = (): void => {
    if (disposed) return;
    body.textContent = '';
    body.append(
      closeButton(close),
      el('div', { class: 'modal-title' }, 'Feed Shed'),
      el('div', { class: 'modal-sub' }, 'Tomorrow’s ration sets tomorrow’s energy.'),
      ...FEED_PLAN_IDS.map((id) => {
        const plan = FEED_PLANS[id];
        const current = sim.state.economy.feedPlan === id;
        return el(
          'button',
          {
            class: `list-row button ${current ? 'selected' : ''}`,
            onclick: () => bus.dispatch({ kind: 'setFeedPlan', plan: id }),
          },
          el('span', { class: 'grow' }, plan.name),
          el('span', {}, plan.costCents === 0 ? 'free' : `${formatMoney(plan.costCents)}/day`),
          el('span', { class: 'energy' }, `⚡ ${plan.energy}`),
          el('span', { class: 'muted' }, `${Math.floor(plan.energy / 4)} session${plan.energy === 4 ? '' : 's'}`),
        );
      }),
    );
  };
  render();
  return { dispose: close, refresh: render };
}

export interface BoardHandlers {
  onRide(eventId: number): void;
}

/** Notice Board: the racing calendar, one season out. */
export function showNoticeBoard(
  root: HTMLElement,
  sim: RanchSim,
  bus: CommandBus<RanchCommand>,
  handlers: BoardHandlers,
): ModalHandle {
  let disposed = false;
  const close = (): void => {
    disposed = true;
    disposeOf(backdrop);
  };
  const { backdrop, body } = openModal(root, 'board-modal', () => close());

  const render = (): void => {
    if (disposed) return;
    body.textContent = '';
    const cards = scheduleWindow(sim);
    const canAdvance =
      !sim.hasEnteredRaceToday() &&
      sim.state.entries.some((entry) => Math.floor(entry.eventId / 10) > sim.state.meta.day);
    body.append(
      closeButton(close),
      el('div', { class: 'modal-title' }, 'Notice Board'),
      el('div', { class: 'modal-sub' }, 'One race per horse each day. Saddle another horse to enter another race; scratches forfeit the fee.'),
      ...(canAdvance
        ? [
            el(
              'button',
              { class: 'button', onclick: () => bus.dispatch({ kind: 'advanceToNextRace' }) },
              'Advance to next entered race',
            ),
          ]
        : []),
      ...cards.map((card) => {
        let action: HTMLElement;
        if (card.canRide) {
          action = el(
            'button',
            {
              class: 'button primary',
              onclick: () => {
                close();
                handlers.onRide(card.eventId);
              },
            },
            'Ride!',
          );
        } else if (card.entered && card.canWithdraw) {
          action = el(
            'button',
            { class: 'button', onclick: () => bus.dispatch({ kind: 'withdrawRace', eventId: card.eventId }) },
            'Scratch',
          );
        } else if (card.entered) {
          action = el('span', { class: 'muted' }, card.enteredHorseName ?? 'entered');
        } else if (card.locked) {
          action = el('span', { class: 'muted' }, '🔒 3 wins below');
        } else if (card.canEnter) {
          action = el(
            'button',
            { class: 'button', onclick: () => bus.dispatch({ kind: 'enterRace', eventId: card.eventId }) },
            'Enter',
          );
        } else {
          action = el('span', { class: 'muted' }, '—');
        }
        return el(
          'div',
          { class: `list-row ${card.isToday ? 'today' : ''}` },
          el('span', { class: 'day-col' }, card.isToday ? 'TODAY' : `Day ${card.day}`),
          el(
            'span',
            { class: 'grow' },
            `${card.leagueName} ${card.className} · ${card.distanceM}m · ${card.going}${card.surface === 'turf' ? ' turf' : ''}${
              card.riskPct > 0 && (card.canEnter || card.canRide || card.entered) ? ` · ⚠${card.riskPct}%` : ''
            }${card.enteredHorseName ? ` · ${card.enteredHorseName}` : ''}`,
          ),
          el('span', {}, card.entryFeeCents === 0 ? 'free' : formatMoney(card.entryFeeCents)),
          el('span', { class: 'money' }, `1st ${formatMoney(card.firstPurseCents)}`),
          action,
        );
      }),
    );
  };
  render();
  return { dispose: close, refresh: render };
}

const LEAGUE_NAMES = ['Local', 'Regional', 'National', 'Grand Derby'];

function statLine(horse: Horse): string {
  return `SPD ${horse.stats.speed} · STA ${horse.stats.stamina} · ACC ${horse.stats.accel} · HRT ${horse.stats.heart}`;
}

function raceFitLine(horse: Horse): string {
  const scores = [
    ['sprint', (horse.stats.speed + horse.stats.accel) / 2],
    ['middle', (horse.stats.speed + horse.stats.stamina + horse.stats.accel + horse.stats.heart) / 4],
    ['route', (horse.stats.stamina + horse.stats.heart) / 2],
  ] as const;
  const sorted = [...scores].sort((a, b) => b[1] - a[1]);
  return sorted[0]![1] - sorted.at(-1)![1] < 1 ? 'balanced race fit' : `${sorted[0]![0]} specialist`;
}

function potentialLine(owner: { potential: Record<StatId, number> }): string {
  const avg =
    (owner.potential.speed + owner.potential.stamina + owner.potential.accel + owner.potential.heart) / 4;
  return `potential ~${avg.toFixed(0)}`;
}

export interface BarnHandlers {
  onPedigree(horseId: number): void;
}

/** The Barn: the whole string — featured sheet, stalls, breeding shed,
 * and the sales. */
export function showBarnModal(
  root: HTMLElement,
  sim: RanchSim,
  bus: CommandBus<RanchCommand>,
  handlers: BarnHandlers,
): ModalHandle {
  let disposed = false;
  let breedingDamId: number | null = null;
  const close = (): void => {
    disposed = true;
    disposeOf(backdrop);
  };
  const { backdrop, body } = openModal(root, 'barn-modal', () => close());

  const render = (): void => {
    if (disposed) return;
    body.textContent = '';
    const state = sim.state;
    const day = state.meta.day;
    const horse = sim.featured();
    const wins = state.record.winsByLeague;
    const children: HTMLElement[] = [closeButton(close), el('div', { class: 'modal-title' }, 'The Barn')];

    // Featured horse sheet.
    if (horse) {
      const nameInput = el('input', { type: 'text', value: horse.name, maxlength: '24' });
      children.push(
        el(
          'div',
          { class: 'rename-row' },
          nameInput,
          el(
            'button',
            { class: 'button', onclick: () => bus.dispatch({ kind: 'renameHorse', name: nameInput.value }) },
            'Rename',
          ),
          el(
            'button',
            { class: 'button', onclick: () => handlers.onPedigree(horse.id) },
            'Pedigree',
          ),
          el(
            'button',
            { class: 'button', onclick: () => bus.dispatch({ kind: 'retireHorse' }) },
            'Retire',
          ),
        ),
        el(
          'div',
          { class: 'modal-sub' },
          `${ageOf(horse, day)}yo ${horse.sex === 'M' ? 'stallion' : 'mare'} · grades ${gradeLine(horse)}${
            traitChips(horse) ? ` · ${traitChips(horse)}` : ''
          } · ${raceFitLine(horse)}`,
        ),
      );
      for (const stat of ['speed', 'stamina', 'accel', 'heart'] as const) {
        const level = horse.stats[stat];
        const cap = sim.statCap(stat);
        const xp = horse.xp[stat];
        const need = xpToNext(level);
        const pct = level >= cap ? 100 : Math.min(100, Math.round((xp / need) * 100));
        children.push(
          el(
            'div',
            { class: 'stat-row' },
            el('span', { class: 'stat-name' }, stat.toUpperCase()),
            el('span', { class: 'stat-level' }, `${level}/${cap}`),
            el('div', { class: 'xp-bar' }, el('div', { class: `xp-fill ${level >= cap ? 'capped' : ''}`, style: `width:${pct}%` })),
          ),
        );
      }
    } else {
      children.push(
        el('div', { class: 'modal-sub urgent-note' }, 'No racer saddled! Promote a barn horse or buy a yearling.'),
      );
    }
    children.push(
      el(
        'div',
        { class: 'record-line' },
        `Stable: ${state.record.starts} starts · ${wins[0]}L/${wins[1]}R/${wins[2]}N/${wins[3]}D wins · ${formatMoney(state.record.earningsCents)} earned`,
      ),
    );

    // Stalls.
    const barn = sim.barnHorses();
    children.push(
      el('div', { class: 'modal-section' }, `STALLS ${barn.length}/${sim.barnStalls()}`),
      ...barn.map((occupant) => {
        const age = ageOf(occupant, day);
        const status =
          age < 2 ? ' · foal' : occupant.retired ? ' · retired' : '';
        return el(
          'div',
          { class: 'list-row' },
          el(
            'span',
            { class: 'grow' },
            `${occupant.name} · ${occupant.sex === 'M' ? '♂' : '♀'} ${age}yo${status} · ${gradeLine(occupant)}${
              traitChips(occupant) ? ` · ${traitChips(occupant)}` : ''
            }`,
          ),
          el('span', { class: 'muted' }, `${statLine(occupant)} · ${raceFitLine(occupant)}`),
          el('button', { class: 'button', onclick: () => handlers.onPedigree(occupant.id) }, 'Tree'),
          ...(occupant.retired || age < 2
            ? []
            : [
                el(
                  'button',
                  { class: 'button', onclick: () => bus.dispatch({ kind: 'promoteHorse', horseId: occupant.id }) },
                  'Saddle up',
                ),
              ]),
          el(
            'button',
            { class: 'button', onclick: () => bus.dispatch({ kind: 'sellHorse', horseId: occupant.id }) },
            `Sell ${formatMoney(horseValueCents(occupant, day))}`,
          ),
        );
      }),
    );

    // The breeding shed — unlocked by a Grand Derby win.
    const derbyWon = (state.record.winsByLeague[3] ?? 0) >= 1;
    children.push(el('div', { class: 'modal-section' }, 'BREEDING SHED'));
    if (!derbyWon) {
      children.push(
        el('div', { class: 'muted' }, 'Win the Grand Derby to found a bloodline.'),
      );
    } else {
      for (const pending of state.pendingFoals) {
        const dam = sim.horseById(pending.damId);
        const sire = sim.horseById(pending.sireId);
        children.push(
          el('div', { class: 'muted' }, `In foal: ${dam?.name ?? '?'} × ${sire?.name ?? '?'} — due at the season turn.`),
        );
      }
      const mares = barn.filter(
        (candidate) =>
          candidate.sex === 'F' &&
          ageOf(candidate, day) >= 3 &&
          !candidate.bredThisSeason &&
          !state.pendingFoals.some((pending) => pending.damId === candidate.id),
      );
      if (mares.length === 0 && state.pendingFoals.length === 0) {
        children.push(el('div', { class: 'muted' }, 'No mare in the barn ready to breed (3yo+, once a season).'));
      }
      for (const mare of mares) {
        if (breedingDamId === mare.id) {
          const ownSires = barn.filter(
            (candidate) => candidate.sex === 'M' && ageOf(candidate, day) >= 3 && candidate.id !== mare.id,
          );
          const studs = state.studIds
            .map((id) => sim.horseById(id))
            .filter((stud): stud is Horse => stud !== null);
          children.push(
            el('div', { class: 'list-row' }, el('span', { class: 'grow' }, `Choose a sire for ${mare.name}:`)),
            ...ownSires.map((sire) =>
              el(
                'button',
                {
                  class: 'list-row button',
                  onclick: () => {
                    breedingDamId = null;
                    bus.dispatch({ kind: 'breed', damId: mare.id, sireId: sire.id });
                  },
                },
                el('span', { class: 'grow' }, `${sire.name} (yours) · ${gradeLine(sire)}`),
                el('span', { class: 'money' }, 'free'),
              ),
            ),
            ...studs.map((stud) =>
              el(
                'button',
                {
                  class: 'list-row button',
                  onclick: () => {
                    breedingDamId = null;
                    bus.dispatch({ kind: 'breed', damId: mare.id, sireId: stud.id });
                  },
                },
                el('span', { class: 'grow' }, `${stud.name} · ${statLine(stud)}`),
                el('span', { class: 'money' }, formatMoney(studFeeCents(stud))),
              ),
            ),
            el(
              'button',
              {
                class: 'button',
                onclick: () => {
                  breedingDamId = null;
                  render();
                },
              },
              'Never mind',
            ),
          );
        } else {
          children.push(
            el(
              'div',
              { class: 'list-row' },
              el('span', { class: 'grow' }, `${mare.name} · ${ageOf(mare, day)}yo mare · ${gradeLine(mare)}`),
              el(
                'button',
                {
                  class: 'button',
                  onclick: () => {
                    breedingDamId = mare.id;
                    render();
                  },
                },
                'Breed…',
              ),
            ),
          );
        }
      }
    }
    if (state.stableLevel < 4) {
      const cost = STABLE_DEF.upgradeCostsCents[state.stableLevel - 1]!;
      children.push(
        el(
          'button',
          { class: 'button', onclick: () => bus.dispatch({ kind: 'upgradeStable' }) },
          `Add stalls — ${formatMoney(cost)} (${stableStalls(state.stableLevel + 1)} total)`,
        ),
      );
    }

    // The vet clinic.
    children.push(el('div', { class: 'modal-section' }, `VET CLINIC${state.vetLevel > 0 ? ` · Lv${state.vetLevel}` : ''}`));
    if (state.vetLevel === 0) {
      children.push(el('div', { class: 'muted' }, 'Unbuilt — racing tired is a blind gamble.'));
    }
    if (state.vetLevel < 3) {
      const vetCosts = [50_000, 200_000, 600_000];
      const perks = ['reads exact fatigue', 'shorter layoffs + bloodline tests', 'shortest layoffs + one patch-up a season'];
      children.push(
        el(
          'button',
          { class: 'button', onclick: () => bus.dispatch({ kind: 'upgradeVet' }) },
          `${state.vetLevel === 0 ? 'Build' : 'Upgrade'} vet — ${formatMoney(vetCosts[state.vetLevel]!)} (${perks[state.vetLevel]})`,
        ),
      );
    }
    if (state.vetLevel >= 2 && horse) {
      children.push(
        el(
          'button',
          { class: 'button', onclick: () => bus.dispatch({ kind: 'buyBloodlineTest', horseId: horse.id }) },
          `Bloodline test ${horse.name} — ${formatMoney(15_000)}`,
        ),
      );
    }
    if (state.vetLevel >= 3 && horse?.injury) {
      children.push(
        el(
          'button',
          { class: 'button primary', onclick: () => bus.dispatch({ kind: 'vetPatchUp' }) },
          `Patch her up (halves the ${horse.injury.daysLeft}-day layoff)`,
        ),
      );
    }

    // The season's yearling sale.
    children.push(el('div', { class: 'modal-section' }, 'YEARLING SALE (fresh each season)'));
    if (state.yearlings.length === 0) {
      children.push(el('div', { class: 'muted' }, 'Sold out — new stock next season.'));
    }
    for (const lot of state.yearlings) {
      children.push(
        el(
          'div',
          { class: 'list-row' },
          el(
            'span',
            { class: 'grow' },
            `${lot.name} · ${lot.sex === 'M' ? 'colt' : 'filly'} · auctioneer says ${STAT_ORDER.map((stat) => gradeOf(lot.estimates[stat])).join('')}`,
          ),
          el(
            'button',
            { class: 'button', onclick: () => bus.dispatch({ kind: 'buyHorse', lotId: lot.lotId }) },
            `Buy ${formatMoney(lot.priceCents)}`,
          ),
        ),
      );
    }

    // Recent results.
    children.push(
      ...state.record.log.slice(0, 6).map((entry) =>
        el(
          'div',
          { class: 'list-row log' },
          el('span', { class: 'day-col' }, `Day ${entry.day}`),
          el('span', { class: 'grow' }, `${sim.horseById(entry.horseId)?.name ?? 'Horse'} · ${LEAGUE_NAMES[entry.league] ?? '?'}`),
          el('span', {}, `${entry.place}${entry.place === 1 ? 'st' : entry.place === 2 ? 'nd' : entry.place === 3 ? 'rd' : 'th'}`),
          el('span', {}, formatTime(entry.timeS)),
          el('span', { class: 'money' }, entry.purseCents > 0 ? formatMoney(entry.purseCents) : ''),
        ),
      ),
    );
    body.append(...children);
  };
  render();
  return { dispose: close, refresh: render };
}

export interface FacilityHandlers {
  /** Launch this facility's training session. */
  onTrain: () => void;
}

/** Three generations in a bracket — the bloodline made visible. */
export function showPedigreeModal(
  root: HTMLElement,
  sim: RanchSim,
  horseId: number,
  handlers: { onBack: () => void },
): ModalHandle {
  let disposed = false;
  const close = (): void => {
    disposed = true;
    disposeOf(backdrop);
    handlers.onBack();
  };
  const { backdrop, body } = openModal(root, 'pedigree-modal', () => close());

  const cell = (horse: Horse | null, generation: number): HTMLElement => {
    if (!horse) return el('div', { class: 'ped-cell empty' }, '— unknown —');
    const swatch = el('span', { class: 'swatch' });
    swatch.style.background = cssColor(
      coatPaletteFor(coatPhenotype(horse.genotype.coat)).colors[2] ?? 0x8f5b33,
    );
    return el(
      'div',
      { class: `ped-cell gen${generation}` },
      swatch,
      el('span', { class: 'ped-name' }, `${horse.name} ${horse.sex === 'M' ? '♂' : '♀'}`),
      el('span', { class: 'ped-line' }, `${gradeLine(horse)} · ${horse.career.winsByLeague.reduce((a, b) => a + b, 0)} wins`),
      ...(traitChips(horse) ? [el('span', { class: 'ped-line' }, traitChips(horse))] : []),
    );
  };

  const render = (): void => {
    if (disposed) return;
    body.textContent = '';
    const view = pedigreeOf(sim, horseId);
    if (!view) {
      close();
      return;
    }
    body.append(
      closeButton(close),
      el('div', { class: 'modal-title' }, `${view.subject.name} — bloodline`),
      el(
        'div',
        { class: 'ped-grid' },
        el('div', { class: 'ped-col' }, cell(view.subject, 0)),
        el('div', { class: 'ped-col' }, ...view.generations[0]!.map((h) => cell(h, 1))),
        el('div', { class: 'ped-col' }, ...view.generations[1]!.map((h) => cell(h, 2))),
        el('div', { class: 'ped-col' }, ...view.generations[2]!.map((h) => cell(h, 3))),
      ),
      el(
        'div',
        { class: 'modal-foot' },
        el('button', { class: 'button primary', onclick: close }, 'Back to the barn'),
      ),
    );
  };
  render();
  return { dispose: () => disposeOf(backdrop), refresh: render };
}

/** One modal serves all four facilities: train, and upgrade the cap. */
export function showFacilityModal(
  root: HTMLElement,
  sim: RanchSim,
  bus: CommandBus<RanchCommand>,
  facility: FacilityId,
  handlers: FacilityHandlers,
): ModalHandle {
  let disposed = false;
  const close = (): void => {
    disposed = true;
    disposeOf(backdrop);
  };
  const { backdrop, body } = openModal(root, 'facility-modal', () => close());
  const def = FACILITY_DEFS[facility];

  const render = (): void => {
    if (disposed) return;
    body.textContent = '';
    const state = sim.state;
    const horse = sim.featured();
    const level = state.facilities[facility];
    const stat = def.stat;
    const statLevel = horse?.stats[stat] ?? 0;
    const cap = sim.statCap(stat);
    const xp = horse?.xp[stat] ?? 0;
    const need = xpToNext(Math.max(1, statLevel));
    const pct = statLevel >= cap ? 100 : Math.min(100, Math.round((xp / need) * 100));
    const raceDay = horse ? sim.hasEnteredRaceToday(horse.id) : false;

    const mode = sim.nextTrainingMode(facility, horse?.id ?? null);
    const game = mode ? MINIGAMES[mode] : null;
    const energyCost = horse && game ? trainingEnergyCost(game.energyCost, sim.traitsOf(horse)) : 4;
    const trainControls: HTMLElement[] = [
      el(
        'button',
        {
          class: 'button primary',
          onclick: () => {
            if (!horse || !game || raceDay || state.energy.current < energyCost) return;
            close();
            handlers.onTrain();
          },
        },
        horse && game ? `Play ${game.name} (⚡${energyCost})` : 'No racer — visit the Barn',
      ),
    ];
    if (horse && mode && horse.masteredTrainingModes.includes(mode)) {
      trainControls.push(
        el(
          'button',
          { class: 'button', onclick: () => bus.dispatch({ kind: 'routineTraining', facility }) },
          `Routine ${game!.name} — Silver (⚡${energyCost})`,
        ),
      );
    }
    if (level < 4) {
      const cost = def.upgradeCostsCents[level - 1]!;
      const nextCap = def.caps[level] ?? 20;
      trainControls.push(
        el(
          'button',
          { class: 'button', onclick: () => bus.dispatch({ kind: 'upgradeFacility', facility }) },
          `Upgrade to Lv${level + 1} — ${formatMoney(cost)} (cap ${nextCap})`,
        ),
      );
    }

    body.append(
      closeButton(close),
      el('div', { class: 'modal-title' }, `${def.name} · Lv${level}`),
      el(
        'div',
        { class: 'stat-row' },
        el('span', { class: 'stat-name' }, stat.toUpperCase()),
        el('span', { class: 'stat-level' }, `${statLevel}/${cap}`),
        el('div', { class: 'xp-bar' }, el('div', { class: `xp-fill ${statLevel >= cap ? 'capped' : ''}`, style: `width:${pct}%` })),
      ),
      el(
        'div',
        { class: 'modal-sub' },
        raceDay
          ? `${horse?.name ?? 'This horse'} is entered today — saddle another horse to train.`
          : `Energy ⚡${state.energy.current}/${state.energy.max} · drill ${sim.trainingCountToday(horse?.id ?? 0, facility) + 1} rotates after each session · Gold unlocks Routine`,
      ),
      el('div', { class: 'modal-foot col' }, ...trainControls),
    );
  };
  render();
  return { dispose: close, refresh: render };
}
